import React, {
    useEffect, useRef, useState,
} from 'react'
import {SearchAutocompleteItem} from './types'
import SearchAutocompleteView from './SearchAutocompleteView'

const SEARCH_DEFAULT_DELAY = 300

interface SearchAutocompleteProps {
    multiselect?: boolean; // ability to select more then one result
    clearAfterSelection?: boolean; // clear input field after selecting item
    label?: string; // label to be displayed before search input
    placeholder?: string; // input placeholder
    featuredItems?: Array<SearchAutocompleteItem>; // items displayed initially before typing anything into search field
    items: Array<SearchAutocompleteItem>; // all the items, we will perfom searching amongs them
    Template: React.FC<SearchAutocompleteItem>; // template used for search result item
    CustomNoResults?: React.FC<{searchValue: string,
    handleSelectItem: (item: SearchAutocompleteItem) => (ev: React.MouseEvent) => void}>, // custom no results component, displayed if customNoResultsShowLogic function will be true
    CustomResultsHeader?: React.FC; // custom results header component
    customNoResultsShowLogic?: (searchValue: string, items: Array<SearchAutocompleteItem>) => boolean; // logic when CustomNoResults component shoudl be shown
    customResultsHeaderShowLogic?: (searchValue: string, items: Array<SearchAutocompleteItem>) => boolean; // logic when CustomNoResultsHeader should be shown
    itemSelected: (item: SearchAutocompleteItem) => void; // handler executed after selecting item
    InputIcon?: React.FC;
    defaultSearchValue?: string;
    dropdownHeight?: number;
    tabIndex?: number;
}


const SearchAutocomplete = (props: SearchAutocompleteProps) => {
    const {
        multiselect, label, placeholder, items, featuredItems = [],
        Template, CustomNoResults, customNoResultsShowLogic, CustomResultsHeader, customResultsHeaderShowLogic,
        clearAfterSelection, itemSelected, InputIcon, defaultSearchValue, dropdownHeight, tabIndex,
    } = props


    const [autocompleteOpen, setAutocompleteOpen] = useState(false)
    const [itemsToDisplay, setItemsToDisplay] = useState<Array<SearchAutocompleteItem>>([])
    const [searchValue, setSearchValue] = useState(defaultSearchValue || '')
    const [selectedItems, setSelectedItems] = useState<Array<SearchAutocompleteItem>>([])

    const autocompleteInputRef = useRef<HTMLInputElement>(null)
    const autocompleteResultsRef = useRef<HTMLDivElement>(null)

    const searchTimerRef = useRef<number | undefined | NodeJS.Timeout>()
    const focusTimeoutRef = useRef<number | undefined | NodeJS.Timeout>()

    const handleChangeSearchValue = (ev: React.ChangeEvent<HTMLInputElement>) => setSearchValue(ev.target.value)

    const handleSelectItem = (item: SearchAutocompleteItem) => (ev: React.MouseEvent) => {
        ev.preventDefault()

        if (multiselect) {
            const selectedItemsTemp = [...selectedItems]
            const itemIndex = selectedItemsTemp.findIndex((selectedItem) => selectedItem.id === item.id)
            if (itemIndex === -1) {
                setSelectedItems([...selectedItemsTemp, item])
            } else {
                selectedItemsTemp.splice(itemIndex, 1)
                setSelectedItems([...selectedItemsTemp])
            }

            itemSelected({
                ...item,
                selected: itemIndex === -1,
            })
            setSearchValue('')
        } else {
            itemSelected(item)
            setSearchValue(item.displayName || item.name)
            setAutocompleteOpen(false)
        }

        if (clearAfterSelection) {
            setSearchValue('')
        }
    }

    const filterSearchResults = () => {
        if (featuredItems.length > 0 && !searchValue) {
            setItemsToDisplay(featuredItems)
        } else {
            const filteredItems = items.filter(
                (item) => item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.content.toLowerCase().includes(searchValue.toLowerCase()) ||
                (item.additionalFilterContent || ')').toLowerCase().includes(searchValue.toLowerCase()),
            )
            setItemsToDisplay(filteredItems)
        }
    }

    const searchForResults = () => {
        clearTimeout(searchTimerRef?.current)
        searchTimerRef.current = setTimeout(() => {
            filterSearchResults()
        }, SEARCH_DEFAULT_DELAY)
    }

    useEffect(() => {
        const keyHandler = (ev: KeyboardEvent) => {
            const isEscPressed = ev.key === 'Escape'

            if (isEscPressed) {
                ev.stopPropagation()
                ev.preventDefault()
                setAutocompleteOpen(false)
                autocompleteInputRef?.current?.blur()
            }
        }

        autocompleteInputRef?.current?.addEventListener('keydown', keyHandler)
        autocompleteResultsRef?.current?.addEventListener('keydown', keyHandler)


        const clearFocusTimeout = () => {
            clearTimeout(focusTimeoutRef.current)
        }

        const setFocusTimeout = () => {
            focusTimeoutRef.current = setTimeout(() => {
                setAutocompleteOpen(false)
            }, 0)
        }

        autocompleteInputRef?.current?.addEventListener('focusin', clearFocusTimeout)
        autocompleteResultsRef?.current?.addEventListener('focusin', clearFocusTimeout)
        autocompleteInputRef?.current?.addEventListener('focusout', setFocusTimeout)
        autocompleteResultsRef?.current?.addEventListener('focusout', setFocusTimeout)
    }, [])


    useEffect(() => {
        const keyHandler = (ev: KeyboardEvent) => {
            const isEnterPressed = ev.key === 'Enter'

            if (isEnterPressed) {
                ev.stopPropagation()
                ev.preventDefault()
            }

            if (isEnterPressed && itemsToDisplay.length === 1 && autocompleteOpen) {
                itemSelected(itemsToDisplay[0])
                setSearchValue('')
            }
        }

        autocompleteInputRef?.current?.addEventListener('keydown', keyHandler)
        return () => {
            autocompleteInputRef?.current?.removeEventListener('keydown', keyHandler)
        }
    }, [itemsToDisplay, autocompleteOpen])


    useEffect(() => {
        searchForResults()
    }, [searchValue, items, featuredItems])

    useEffect(() => {
        if (defaultSearchValue) {
            setSearchValue(defaultSearchValue)
        }
    }, [defaultSearchValue])

    return (
        <SearchAutocompleteView
            CustomNoResults={CustomNoResults}
            CustomResultsHeader={CustomResultsHeader}
            InputIcon={InputIcon}
            Template={Template}
            autocompleteInputRef={autocompleteInputRef}
            autocompleteOpen={autocompleteOpen}
            autocompleteResultsRef={autocompleteResultsRef}
            customNoResultsShowLogic={customNoResultsShowLogic}
            customResultsHeaderShowLogic={customResultsHeaderShowLogic}
            dropdownHeight={dropdownHeight}
            featuredActive={searchValue === '' && featuredItems.length > 0}
            handleChangeSearchValue={handleChangeSearchValue}
            handleSelectItem={handleSelectItem}
            items={itemsToDisplay}
            label={label}
            placeholder={placeholder}
            searchValue={searchValue}
            selectedItems={selectedItems}
            setAutocompleteOpen={() => setAutocompleteOpen(true)}
            tabIndex={tabIndex}
        />
    )
}


SearchAutocomplete.defaultProps = {
    CustomNoResults: null,
    CustomResultsHeader: null,
    InputIcon: null,
    clearAfterSelection: false,
    customNoResultsShowLogic: () => false,
    customResultsHeaderShowLogic: () => false,
    defaultSearchValue: '',
    dropdownHeight: undefined,
    featuredItems: [],
    label: '',
    multiselect: false,
    placeholder: '',
    tabIndex: undefined,
}


export default SearchAutocomplete
